import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Hourglass } from "lucide-react";

import { t } from "../i18n";

/*
 * Premium module card — used for Drawing / Language / Math Tools.
 * Preserves the existing base color while layering:
 *   • a soft diagonal light streak (SVG gradient)
 *   • a low-opacity dot-grid pattern in the bottom-right corner (AI-ish)
 *   • a glass-morphism icon tile with an inner highlight and a corner sparkle
 *   • a refined "Open" pill CTA on the right with slate arrow
 * All accents are pulled from the `accent` object per card so each feels
 * unique while remaining a cohesive family.
 */

const PALETTES = {
  mind: {
    baseBg:      "bg-gradient-to-br from-indigo-400 via-violet-500 to-slate-800",
    highlight:   "from-white/40 via-white/5 to-transparent",
    dotColor:    "#c4b5fd",  // violet-300
    ringGlow:    "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_20px_-6px_rgba(79,70,229,0.55)]",
    sparkle:     "text-amber-400",
    ctaText:     "text-indigo-700",
    ctaHover:    "hover:bg-indigo-50",
    iconAnim:    { animate: { scale: [1, 1.08, 1], rotate: [-2, 2, -2] }, duration: 4.8 },
    titleClass:  "text-white",
    descClass:   "text-white/85",
  },
  drawing: {
    baseBg:      "bg-pink-300",
    highlight:   "from-white/70 via-white/10 to-transparent",
    dotColor:    "#f472b6",   // pink-400
    ringGlow:    "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_20px_-6px_rgba(236,72,153,0.35)]",
    sparkle:     "text-fuchsia-500",
    ctaText:     "text-fuchsia-600",
    ctaHover:    "hover:bg-fuchsia-50",
    iconAnim:    { animate: { rotate: [-8, 8, -8], y: [0, -2, 0] }, duration: 3.2 },
  },
  language: {
    baseBg:      "bg-sky-300",
    highlight:   "from-white/70 via-white/10 to-transparent",
    dotColor:    "#38bdf8",  // sky-400
    ringGlow:    "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_20px_-6px_rgba(14,165,233,0.35)]",
    sparkle:     "text-indigo-500",
    ctaText:     "text-indigo-600",
    ctaHover:    "hover:bg-indigo-50",
    iconAnim:    { animate: { scale: [1, 1.10, 1], rotate: [-3, 3, -3] }, duration: 3.0 },
  },
  math: {
    baseBg:      "bg-yellow-300",
    highlight:   "from-white/70 via-white/10 to-transparent",
    dotColor:    "#f59e0b",  // amber-500
    ringGlow:    "shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_10px_20px_-6px_rgba(245,158,11,0.35)]",
    sparkle:     "text-amber-500",
    ctaText:     "text-amber-600",
    ctaHover:    "hover:bg-amber-50",
    iconAnim:    { animate: { y: [0, -3, 0, -3, 0], rotate: [-4, 4, -4] }, duration: 2.6 },
  },
};

export default function ModuleCard({ slug, to, icon: Icon, titleKey, descKey, badgeKey, active, lang, onNavigate, index = 0 }) {
  const palette = PALETTES[slug] || PALETTES.drawing;

  return (
    <motion.button
      data-testid={`module-${slug}-btn`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.06 * index, type: "spring", stiffness: 160, damping: 18 }}
      whileTap={{ scale: 0.985 }}
      whileHover={{ y: -2 }}
      onClick={() => onNavigate(to)}
      className={`group relative w-full overflow-hidden text-left rounded-[28px] p-4 border-4 border-white
        ${palette.baseBg}
        shadow-[0_10px_0_-2px_rgba(0,0,0,0.06),0_20px_40px_-14px_rgba(15,23,42,0.18)]
        transition-all duration-300 ${!active ? "opacity-90" : ""}`}
    >
      {/* --- Layered background ornaments --- */}
      {/* diagonal light streak */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${palette.highlight}`}
      />
      {/* corner dot-grid — AI vibe, low opacity */}
      <svg
        aria-hidden
        className="pointer-events-none absolute -bottom-4 -right-2 w-40 h-40 opacity-[0.22] mix-blend-multiply"
        viewBox="0 0 100 100"
      >
        <defs>
          <pattern id={`dots-${slug}`} x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.2" fill={palette.dotColor} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#dots-${slug})`} />
      </svg>
      {/* soft glow behind icon */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-6 -left-6 w-40 h-40 rounded-full bg-white/45 blur-3xl"
      />

      {/* --- Foreground content --- */}
      <div className="relative flex items-center gap-4">
        {/* Icon tile */}
        <div className="relative shrink-0">
          <div
            className={`w-16 h-16 rounded-2xl bg-white/95 backdrop-blur ring-1 ring-white/70
              flex items-center justify-center ${palette.ringGlow}`}
          >
            <motion.div
              style={{ display: "inline-flex", transformOrigin: "50% 60%" }}
              animate={palette.iconAnim.animate}
              transition={{ duration: palette.iconAnim.duration, repeat: Infinity, ease: "easeInOut" }}
            >
              <Icon size={30} strokeWidth={2.5} className="text-slate-800" />
            </motion.div>
          </div>
          {/* corner sparkle */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.06 * index + 0.3 }}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
          >
            <Sparkles size={11} strokeWidth={3} className={palette.sparkle} />
          </motion.div>
        </div>

        {/* Text block */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-heading text-xl sm:text-[22px] font-bold leading-tight tracking-tight ${palette.titleClass || "text-slate-900"}`}>
            {t(lang, titleKey)}
          </h3>
          <p className={`text-[13px] sm:text-sm font-semibold mt-0.5 line-clamp-2 leading-snug ${palette.descClass || "text-slate-800/85"}`}>
            {t(lang, descKey)}
          </p>
        </div>

        {/* CTA */}
        <div className="shrink-0 self-center">
          {active ? (
            <div
              className={`inline-flex items-center gap-1.5 bg-white ${palette.ctaHover} ${palette.ctaText}
                font-heading text-[11px] font-bold uppercase tracking-[0.12em]
                pl-3 pr-2.5 py-2 rounded-full border-2 border-white
                shadow-[0_4px_0_-1px_rgba(0,0,0,0.08),0_6px_10px_-4px_rgba(0,0,0,0.15)]
                group-active:translate-y-0.5 group-active:shadow-none
                transition-all`}
            >
              <span>{t(lang, badgeKey)}</span>
              <span className="w-5 h-5 rounded-full bg-slate-900/90 text-white flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                <ArrowRight size={12} strokeWidth={3} />
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 bg-white/85 text-slate-700 font-heading text-[11px] font-bold uppercase tracking-[0.12em] px-3 py-2 rounded-full">
              <Hourglass size={11} strokeWidth={3} />
              <span>{t(lang, badgeKey)}</span>
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
}
